
import React, { useEffect, useState, useMemo } from 'react';
import AirplaneIcon from './AirplaneIcon';

const defaultSteps = [
  { label: "Initializing", description: "Architecting your bespoke itinerary..." },
  { label: "Archives", description: "Consulting the archives..." },
  { label: "Logistics", description: "Curating escapes and sanctuaries..." },
  { label: "Itinerary", description: "Designing sensory moments..." },
  { label: "Imagery", description: "Calibrating light and texture..." },
  { label: "Finalizing", description: "Assembling the itinerary..." },
];

const travelTips = [
  { tip: "Book flights 6-8 weeks before departure for the best prices on international routes.", category: "Flights" },
  { tip: "Tuesday and Wednesday are typically the cheapest days to fly.", category: "Flights" },
  { tip: "Hotels often offer their best rates when booked directly through their website.", category: "Hotels" },
  { tip: "Pack a portable charger — your phone is your boarding pass, map, and translator.", category: "Packing" },
  { tip: "Take a photo of your hotel's business card for easy taxi directions back.", category: "Travel Hack" },
  { tip: "Arriving at your destination? The first meal should always be local street food.", category: "Foodie" },
  { tip: "Set a Google Alert for your destination to catch events and festivals.", category: "Planning" },
  { tip: "Roll your clothes instead of folding — you'll fit 30% more in your suitcase.", category: "Packing" },
  { tip: "Use incognito mode when searching flights to avoid dynamic pricing.", category: "Flights" },
  { tip: "Always carry a photocopy of your passport separate from the original.", category: "Safety" },
  { tip: "Download offline maps for your destination before you leave.", category: "Travel Hack" },
  { tip: "The best currency exchange rates are usually at local ATMs, not airport kiosks.", category: "Money" },
];

interface LoadingScreenProps {
  message?: string;
  currentStep?: number;
  destination?: string;
  isComplete?: boolean;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message, currentStep, destination, isComplete }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * travelTips.length));
  const [tipFade, setTipFade] = useState(true);

  // Personalize step descriptions when destination is available
  const steps = useMemo(() => {
    if (!destination) return defaultSteps;
    return [
      { label: "Initializing", description: `Architecting your ${destination} itinerary...` },
      { label: "Archives", description: `Researching hidden gems in ${destination}...` },
      { label: "Logistics", description: `Sourcing flights & hotels for ${destination}...` },
      { label: "Itinerary", description: `Curating experiences for ${destination}...` },
      { label: "Imagery", description: `Capturing the essence of ${destination}...` },
      { label: "Finalizing", description: `Assembling your ${destination} itinerary...` },
    ];
  }, [destination]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % steps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [steps.length]);

  useEffect(() => {
    const timer = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const tipInterval = setInterval(() => {
      setTipFade(false);
      setTimeout(() => {
        setTipIndex((prev) => (prev + 1) % travelTips.length);
        setTipFade(true);
      }, 300); // Reduced from 400ms to 300ms for tighter crossfade
    }, 6000);
    return () => clearInterval(tipInterval);
  }, []);

  const activeStep = currentStep ?? Math.min(messageIndex, steps.length - 1);
  const rawProgress = Math.min(((activeStep + 1) / steps.length) * 100, 100);

  // Cap progress at 95% during loading, jump to 100% only when complete
  // Easing slows down after 50% so bar doesn't appear frozen
  const cappedProgress = isComplete ? 100 : Math.min(rawProgress, 95);
  const easedProgress = isComplete
    ? 100
    : cappedProgress < 50
      ? cappedProgress
      : 50 + (cappedProgress - 50) * 0.6;

  const currentTip = travelTips[tipIndex];

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-charcoal text-ivory px-6">
      {/* Cinematic ambient background with Ken Burns */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?q=60&w=1200&auto=format&fit=crop')] bg-cover bg-center opacity-[0.08] ken-burns-slow" />
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/95 to-charcoal" />
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212,175,55,0.3) 1px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div className="relative z-10 flex flex-col items-center max-w-lg w-full">
        {/* Animated airplane with active float animation */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 bg-gold/20 rounded-full blur-xl animate-pulse" />
          <div style={{ animation: 'float 4s ease-in-out infinite' }}>
            <AirplaneIcon className="w-14 h-14 text-gold relative z-10" />
          </div>
        </div>

        <h2
          className="font-display text-2xl md:text-3xl tracking-[0.3em] mb-2 text-ivory"
          style={{ opacity: 0, animation: 'fadeInUp 800ms cubic-bezier(0.16, 1, 0.3, 1) 100ms forwards' }}
        >AURA TRAVELER</h2>
        <p
          className="font-serif italic text-gold/80 text-sm mb-10"
          style={{ opacity: 0, animation: 'fadeIn 600ms cubic-bezier(0.16, 1, 0.3, 1) 400ms forwards' }}
        >
          {steps[activeStep]?.description || message}
        </p>

        {/* Progress bar - capped at 95% until complete */}
        <div className="w-full max-w-xs mb-3">
          <div className="h-[2px] bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-gold/60 to-gold rounded-full"
              style={{
                width: `${easedProgress}%`,
                transition: isComplete ? 'width 400ms ease-out' : 'width 1000ms ease-out',
              }}
            />
          </div>
        </div>

        {/* Step indicators — staggered entrance */}
        <div className="flex items-center gap-1 mb-10 flex-wrap justify-center">
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              <div
                className="flex flex-col items-center gap-1.5"
                style={{
                  opacity: 0,
                  animation: `fadeInUp 400ms cubic-bezier(0.16, 1, 0.3, 1) ${200 + i * 100}ms forwards`,
                }}
              >
                <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i < activeStep ? 'bg-gold' :
                  i === activeStep ? 'bg-gold animate-pulse scale-125 shadow-[0_0_8px_rgba(212,175,55,0.5)]' :
                  'bg-white/15'
                }`} />
                <span className={`font-mono text-[10px] uppercase tracking-widest transition-colors duration-300 ${
                  i <= activeStep ? 'text-gold' : 'text-white/75'
                }`}>{step.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`h-px w-2.5 md:w-4 lg:w-6 mb-3 transition-colors duration-300 ${
                  i < activeStep ? 'bg-gold/50' : 'bg-white/10'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Travel tip card with crossfade + subtle scale */}
        <div
          className="w-full max-w-sm bg-white/[0.04] border border-white/[0.08] backdrop-blur-sm p-5"
          style={{
            opacity: tipFade ? 1 : 0,
            transform: tipFade ? 'scale(1) translateY(0)' : 'scale(0.99) translateY(4px)',
            transition: tipFade
              ? 'opacity 300ms ease-in, transform 400ms cubic-bezier(0.16, 1, 0.3, 1)'
              : 'opacity 200ms ease-out, transform 200ms ease-out',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/70">{currentTip.category}</span>
            <div className="h-px bg-white/10 flex-grow" />
          </div>
          <p className="font-serif text-xs text-ivory/70 leading-relaxed italic">
            {currentTip.tip}
          </p>
        </div>

        {/* Timer with estimated time remaining */}
        <div className="mt-6 flex flex-col items-center gap-1">
          <div className="flex items-center gap-2">
            <div className="w-1 h-1 rounded-full bg-gold/50 animate-pulse" />
            <span className="font-mono text-[10px] text-white/80">
              {elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`}
            </span>
          </div>
          {elapsed > 2 && elapsed < 90 && (
            <span className="font-mono text-[10px] text-white/40">
              {elapsed < 8 ? "~15-25s remaining" :
               elapsed < 15 ? "~10-20s remaining" :
               elapsed < 25 ? "~5-15s remaining" :
               elapsed < 40 ? "Almost there..." :
               "Finishing up..."}
            </span>
          )}
        </div>
      </div>

      {/* Float animation keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
